// services/userService.js
import api from "./api";

export const getAllUsers = async () => {
  try {
    const response = await api.get("/users/", {
      maxBodyLength: Infinity,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error(
        "Response data:",
        JSON.stringify(error.response.data, null, 2)
      );
      console.error(
        "Response headers:",
        JSON.stringify(error.response.headers, null, 2)
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

export const getUserById = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}/`, {
      maxBodyLength: Infinity,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/users/${userId}/`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const formattedData = {
      username: userData.username,
      first_name: userData.first_name,
      last_name: userData.last_name,
      is_active: userData.is_active,
      role: userData.role,
      org: userData.org.id || userData.org, // Handle both object and ID cases
      assigned_orgs: userData.assigned_orgs || [],
    };

    // Only include password if it's provided
    if (userData.password) {
      formattedData.password = userData.password;
    }

    const response = await api.post("/users/", formattedData, {
      maxBodyLength: Infinity,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
      console.error("Response headers:", error.response.headers);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await api.delete(`/users/${userId}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

export const bulkDeleteUsers = async (userIds) => {
  try {
    // Delete users one by one using the existing deleteUser function
    const deletePromises = userIds.map((id) => deleteUser(id));
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error("Error bulk deleting users:", error);
    throw error;
  }
};

// Optional: Add pagination support
export const getUsersWithPagination = async ({
  page = 1,
  pageSize = 10,
  search = "",
  ordering = "-date_joined",
  role = "",
  organization = "",
}) => {
  try {
    const params = new URLSearchParams({
      page,
      page_size: pageSize,
      search,
      ordering,
    });

    if (role) params.append("role", role);
    if (organization) params.append("organization", organization);

    const response = await api.get("/users/", {
      params,
      maxBodyLength: Infinity,
    });

    return {
      results: response.data.results,
      count: response.data.count,
      next: response.data.next,
      previous: response.data.previous,
    };
  } catch (error) {
    console.error("Error fetching users with pagination:", error);
    throw error;
  }
};

// Optional: Add user roles management
export const assignUserRoles = async (userId, roles) => {
  try {
    const response = await api.post(`/users/${userId}/roles/`, { roles });
    return response.data;
  } catch (error) {
    console.error("Error assigning user roles:", error);
    throw error;
  }
};

// Optional: Add user organization management
export const updateUserOrganization = async (userId, orgId) => {
  try {
    const response = await api.put(`/users/${userId}/organization/`, {
      org: orgId,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating user organization:", error);
    throw error;
  }
};

// Optional: Add user activation/deactivation
export const toggleUserActive = async (userId, isActive) => {
  try {
    const response = await api.patch(`/users/${userId}/`, {
      is_active: isActive,
    });
    return response.data;
  } catch (error) {
    console.error("Error toggling user active status:", error);
    throw error;
  }
};
